import React from 'react'
import classNames from 'classnames'

import { ToastType, useToastStore } from '@/store/toastStore'
import CheckIcon from '@/components/Svg/CheckIcon'
import { Icons } from '@/components/Shared/icons'
import LoadingSpinner from '@/components/Svg/LoadingSpinner'

const ToastContainer: React.FC = () => {
  const { toasts, removeToast } = useToastStore()

  if (toasts.length === 0) return null

  const getToastStyles = (type: ToastType) => {
    switch (type) {
      case 'success':
        return 'bg-coolGray-22 text-white'
      case 'error':
        return 'bg-status-negative text-white'
      case 'warning':
        return 'bg-status-cautionary text-white'
      case 'info':
        return 'bg-[#292A2D] text-white'
      case 'loading':
        return 'bg-primary-normal text-white'
      case 'alarm':
        return 'bg-white text-label-neutral !shadow-emphasize'
      default:
        return 'bg-coolGray-22 text-white'
    }
  }

  const getToastIcon = (type: ToastType) => {
    switch (type) {
      case 'success':
        return (
          <div className="w-5 h-5 rounded-full bg-static-white flex items-center justify-center">
            <Icons.Check className="w-3 h-3 fill-coolGray-22" />
          </div>
        )
      case 'error':
        return <Icons.InfoTriangle className="w-4 h-4 fill-white" />
      case 'warning':
        return <Icons.InfoTriangle className="w-4 h-4 fill-white" />
      case 'info':
        return <Icons.EmptySend className="w-4 h-4 fill-white" />
      case 'loading':
        return <LoadingSpinner className="w-4 h-4" color="white" />
      case 'alarm':
        return <Icons.CheckCircle className="w-4 h-4 fill-label-neutral" />
      default:
        return <CheckIcon className="w-4 h-4" fill="white" />
    }
  }

  return (
    <div
      // z-1100 keeps toasts above full-screen modals (e.g. FundSelectModal at
      // z-1000) so success/error feedback isn't hidden behind them.
      className={classNames(
        toasts.some((toast) => toast.type === 'alarm')
          ? 'right-4 top-[58px]'
          : 'left-1/2 transform -translate-x-1/2',
        'fixed top-[26px] z-[1100] space-y-2'
      )}
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`
            flex items-center p-[10px] rounded-lg shadow-lg
            ${getToastStyles(toast.type)}
          `}
          onClick={() => {
            if (toast.onClick) {
              toast.onClick()
              removeToast(toast.id)
            }
          }}
          role={toast.onClick ? 'button' : undefined}
          tabIndex={toast.onClick ? 0 : undefined}
          onKeyDown={(e) => {
            if (toast.onClick && e.key === 'Enter') {
              toast.onClick()
              removeToast(toast.id)
            }
          }}
        >
          {/* Icon */}
          <div className="flex-shrink-0">{getToastIcon(toast.type)}</div>

          {/* Text */}
          <div className="flex-1 text-body-15-regular ml-[10px] whitespace-pre-wrap">
            {toast.text}
          </div>

          {/* Dismiss button for alarm toasts */}
          {toast.type === 'alarm' && (
            <button
              className="flex-shrink-0 ml-2 p-1 rounded hover:bg-gray-100"
              onClick={(e) => {
                e.stopPropagation()
                removeToast(toast.id)
              }}
            >
              <Icons.Close className="w-3 h-3 fill-label-neutral" />
            </button>
          )}
        </div>
      ))}
    </div>
  )
}

export default ToastContainer
